import React, { useCallback, useState } from "react";
import parse from "html-react-parser";
import { T, useT } from "@transifex/react";
import { RiskLevel, VideoData, ContentItem } from "../InterestCard";
import { ContentItemsCarousel } from "../InterestCard/ContentItemsCarousel";
import { categoryIcons, CheckIcon, InfoCircleIcon, CheckCircleIcon } from "./icons";
import {
  CardWrapper,
  TileHeader,
  IconWrapper,
  ContentArea,
  TitleRow,
  TileTitle,
  RiskBadge,
  ActionBadge,
  ShortText,
  ActionsArea,
  SelectionToggle,
  ExpandChevron,
  ExpandedContent,
  NextStepsSection,
  NextStepsTitle,
  NextStepsIcon,
  NextStepsBody,
  ContentItemsArea,
  VideosArea,
  VideoCard,
  VideoCardTitle,
  LearnMoreToggle,
  LearnMoreText,
  LearnMoreIcon,
  LearnMoreContent,
  RiskFactorsGrid,
  RiskFactorBox,
  RiskFactorHeader,
  RiskFactorIcon,
  RiskFactorList,
  RiskFactorItem,
  BodyHtmlSection,
  InterestIndicatorRow,
  InterestIndicatorCheck,
  InterestIndicatorIcon,
  InterestIndicatorLabel,
  getColorByIndex,
} from "./styles";

// Chevron down icon
const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Lightbulb icon for next steps
const LightbulbIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M9 21h6M12 3a6 6 0 0 0-4 10.5V17h8v-3.5A6 6 0 0 0 12 3z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Warning triangle icon for increasing factors
const WarningTriangleSmall = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 9v4M12 17h.01M21.73 18L13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Check circle icon for decreasing factors
const CheckCircleSmall = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export interface CollapsedInterestCardProps {
  /** Card key identifier */
  cardKey: string;
  /** Index used to assign a color from the palette (cycles after 10) */
  colorIndex: number;
  /** Card title */
  title: string;
  /** Short description text displayed below the title */
  shortText?: string;
  /** Risk level for the badge */
  riskLevel: RiskLevel;
  /** Whether the user has viewed/expanded this card */
  isViewed: boolean;
  /** Whether the card is currently expanded */
  isExpanded: boolean;
  /** Callback when the card header is clicked to toggle expansion */
  onToggle: () => void;
  /** Whether the user has expressed interest */
  isInterested: boolean;
  /** Whether to show the interest toggle */
  showInterestToggle?: boolean;
  /** Callback when interest is toggled */
  onInterestChange?: (checked: boolean) => void;
  /** Optional URL to an SVG icon for the card. When set, overrides category icon. */
  icon?: string;
  /** Next steps header title */
  nextStepsHeader?: string;
  /** Next steps header HTML content */
  nextStepsHeaderContent?: string;
  /** HTML body content (shown in learn more) */
  bodyHtml?: string;
  /** Videos to display */
  videos?: VideoData[];
  /** Dynamic content items */
  contentItems?: ContentItem[];
  /** Factors increasing risk */
  increasingFactors?: string[];
  /** Title for increasing factors */
  increasingFactorsTitle?: string;
  /** Factors decreasing risk */
  decreasingFactors?: string[];
  /** Title for decreasing factors */
  decreasingFactorsTitle?: string;
  /** Label text shown at bottom of expanded card when interest is expressed */
  interestLabel?: string;
  /** URL to an icon shown alongside the interest label (same as request-call icon) */
  interestIcon?: string;
  /** Optional className for additional styling */
  className?: string;
  /** Optional data-testid for testing */
  "data-testid"?: string;
}

export function CollapsedInterestCard({
  cardKey,
  colorIndex,
  title,
  shortText,
  riskLevel,
  isViewed,
  isExpanded,
  onToggle,
  isInterested,
  showInterestToggle = false,
  onInterestChange,
  icon,
  nextStepsHeader,
  nextStepsHeaderContent,
  bodyHtml,
  videos = [],
  contentItems = [],
  increasingFactors = [],
  increasingFactorsTitle,
  decreasingFactors = [],
  decreasingFactorsTitle,
  interestLabel,
  interestIcon,
  className,
  "data-testid": testId,
}: CollapsedInterestCardProps) {
  const t = useT();
  const colors = getColorByIndex(colorIndex);
  const IconComponent = categoryIcons[cardKey];

  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const handleHeaderKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  const handleSelectionClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onInterestChange?.(!isInterested);
    },
    [isInterested, onInterestChange]
  );

  const handleSelectionKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        onInterestChange?.(!isInterested);
      }
    },
    [isInterested, onInterestChange]
  );

  const isHighRisk = riskLevel === "high";

  const hasNextSteps = !!(nextStepsHeader || nextStepsHeaderContent);
  const hasContentItems = contentItems.length > 0;
  const hasVideos = videos.length > 0;
  const hasLearnMoreContent =
    increasingFactors.length > 0 ||
    decreasingFactors.length > 0 ||
    !!bodyHtml;

  return (
    <CardWrapper
      $colors={colors}
      $isViewed={isViewed}
      $isExpanded={isExpanded}
      className={className}
      data-testid={testId || `CollapsedInterestCard-${cardKey}`}
    >
      {/* Clickable header row */}
      <TileHeader
        $isExpanded={isExpanded}
        $headerBackground={colors.headerBackground}
        onClick={onToggle}
        onKeyDown={handleHeaderKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${title} - ${isHighRisk ? t("High risk, action required") : t("Average risk, on track")}${isViewed ? ", viewed" : ""}${isInterested ? ", selected" : ""}`}
      >
        {/* Category icon */}
        <IconWrapper $background={colors.iconBackground} $color={colors.iconColor} $isExpanded={isExpanded}>
          {icon ? (
            <img src={icon} alt="" aria-hidden="true" />
          ) : (
            IconComponent && <IconComponent />
          )}
        </IconWrapper>

        {/* Content area */}
        <ContentArea>
          <TitleRow>
            <TileTitle $isExpanded={isExpanded}>{title}</TileTitle>
            <RiskBadge $isHigh={isHighRisk} $isExpanded={isExpanded} data-testid="CollapsedInterestCard__RiskBadge">
              {isHighRisk ? t("HIGH RISK") : t("AVERAGE RISK")}
            </RiskBadge>
            <ActionBadge $isHigh={isHighRisk} $isExpanded={isExpanded} data-testid="CollapsedInterestCard__ActionBadge">
              {isHighRisk ? (
                <>
                  <InfoCircleIcon />
                  {t("ACTION")}
                </>
              ) : (
                <>
                  <CheckCircleIcon />
                  {t("ON TRACK")}
                </>
              )}
            </ActionBadge>
          </TitleRow>
          {shortText && <ShortText $isExpanded={isExpanded}>{shortText}</ShortText>}
        </ContentArea>

        {/* Right-side actions */}
        <ActionsArea>
          {showInterestToggle && (
            <SelectionToggle
              $checked={isInterested}
              $color={colors.iconColor}
              $isExpanded={isExpanded}
              onClick={handleSelectionClick}
              onKeyDown={handleSelectionKeyDown}
              role="checkbox"
              aria-checked={isInterested}
              aria-label={isInterested ? t("Remove from follow-up") : t("Add to follow-up")}
              tabIndex={0}
            >
              <CheckIcon />
            </SelectionToggle>
          )}
          <ExpandChevron $isExpanded={isExpanded}>
            <ChevronDownIcon />
          </ExpandChevron>
        </ActionsArea>
      </TileHeader>

      {/* Expanded content */}
      {isExpanded && (
        <ExpandedContent data-testid="CollapsedInterestCard__ExpandedContent">
          {/* 1. Next Steps */}
          {hasNextSteps && (
            <NextStepsSection data-testid="CollapsedInterestCard__NextSteps">
              {nextStepsHeader && (
                <NextStepsTitle>
                  <NextStepsIcon>
                    <LightbulbIcon />
                  </NextStepsIcon>
                  {nextStepsHeader}
                </NextStepsTitle>
              )}
              {nextStepsHeaderContent && (
                <NextStepsBody>
                  {parse(nextStepsHeaderContent)}
                </NextStepsBody>
              )}
            </NextStepsSection>
          )}

          {/* 2. Content Items */}
          {hasContentItems && (
            <ContentItemsArea data-testid="CollapsedInterestCard__ContentItems">
              <ContentItemsCarousel items={contentItems} />
            </ContentItemsArea>
          )}

          {/* 2b. Videos */}
          {hasVideos && (
            <VideosArea data-testid="CollapsedInterestCard__Videos">
              {videos.map((video) => (
                <VideoCard key={video.id}>
                  <video
                    src={video.src}
                    controls
                    preload="metadata"
                    aria-label={video.title}
                  />
                  <VideoCardTitle>{video.title}</VideoCardTitle>
                </VideoCard>
              ))}
            </VideosArea>
          )}

          {/* 3. Learn more collapsible */}
          {hasLearnMoreContent && (
            <>
              <LearnMoreToggle
                onClick={() => setLearnMoreOpen(!learnMoreOpen)}
                aria-expanded={learnMoreOpen}
                data-testid="CollapsedInterestCard__LearnMoreToggle"
              >
                <LearnMoreIcon $expanded={learnMoreOpen}>
                  <ChevronDownIcon />
                </LearnMoreIcon>
                <LearnMoreText>
                  {learnMoreOpen ? t("Show less") : t("Learn more")}
                </LearnMoreText>
              </LearnMoreToggle>

              <LearnMoreContent $expanded={learnMoreOpen}>
                {/* Risk Factors */}
                {(increasingFactors.length > 0 || decreasingFactors.length > 0) && (
                  <RiskFactorsGrid data-testid="CollapsedInterestCard__RiskFactors">
                    {increasingFactors.length > 0 && (
                      <RiskFactorBox $type="increasing">
                        <RiskFactorHeader $type="increasing">
                          <RiskFactorIcon $type="increasing">
                            <WarningTriangleSmall />
                          </RiskFactorIcon>
                          {increasingFactorsTitle || (
                            <>
                              <T _str="Factors " />
                              <b><T _str="increasing" /></b>
                              <T _str=" your risk" />
                            </>
                          )}
                        </RiskFactorHeader>
                        <RiskFactorList>
                          {increasingFactors.map((factor, idx) => (
                            <RiskFactorItem key={idx} $type="increasing">
                              {factor}
                            </RiskFactorItem>
                          ))}
                        </RiskFactorList>
                      </RiskFactorBox>
                    )}

                    {decreasingFactors.length > 0 && (
                      <RiskFactorBox $type="decreasing">
                        <RiskFactorHeader $type="decreasing">
                          <RiskFactorIcon $type="decreasing">
                            <CheckCircleSmall />
                          </RiskFactorIcon>
                          {decreasingFactorsTitle || (
                            <>
                              <T _str="Factors " />
                              <b><T _str="decreasing" /></b>
                              <T _str=" your risk" />
                            </>
                          )}
                        </RiskFactorHeader>
                        <RiskFactorList>
                          {decreasingFactors.map((factor, idx) => (
                            <RiskFactorItem key={idx} $type="decreasing">
                              {factor}
                            </RiskFactorItem>
                          ))}
                        </RiskFactorList>
                      </RiskFactorBox>
                    )}
                  </RiskFactorsGrid>
                )}

                {/* Body HTML */}
                {bodyHtml && (
                  <BodyHtmlSection data-testid="CollapsedInterestCard__BodyHtml">
                    {parse(bodyHtml)}
                  </BodyHtmlSection>
                )}
              </LearnMoreContent>
            </>
          )}
          {/* Interest indicator at bottom of expanded card */}
          {showInterestToggle && interestLabel && (
            <InterestIndicatorRow
              $color={colors.iconColor}
              $checked={isInterested}
              onClick={handleSelectionClick}
              onKeyDown={handleSelectionKeyDown}
              role="checkbox"
              aria-checked={isInterested}
              aria-label={isInterested ? t("Remove from follow-up") : t("Add to follow-up")}
              tabIndex={0}
              data-testid="CollapsedInterestCard__InterestIndicator"
            >
              <InterestIndicatorCheck $color={colors.iconColor} $checked={isInterested}>
                <CheckCircleIcon />
              </InterestIndicatorCheck>
              <InterestIndicatorLabel $checked={isInterested}>
                {interestLabel}
              </InterestIndicatorLabel>
              {interestIcon?.trim() && (
                <InterestIndicatorIcon>
                  <img src={interestIcon.trim()} alt="" aria-hidden="true" />
                </InterestIndicatorIcon>
              )}
            </InterestIndicatorRow>
          )}
        </ExpandedContent>
      )}
    </CardWrapper>
  );
}

export { colorPalette, getColorByIndex } from "./styles";
